import React, { Ref, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { ChipFieldCombinedProps, ChipFieldErrorEnum } from './types';
import { Option, RenderToken, RenderTokenProps } from 'react-bootstrap-typeahead/types/types';
import { ChipRecord, ChipCombinedProps } from '../../atoms/Chip/types';
import { Chip } from '../../atoms';
import Typeahead from 'react-bootstrap-typeahead/types/core/Typeahead';
import styles from './ChipField.module.scss';

type ChipFieldPresenterProps = ChipFieldCombinedProps & {
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: SyntheticEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onInputChanged?: (text: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRenderChip?: (option: Option, props: RenderTokenProps, index: number) => JSX.Element;
  selected?: Option[];
  targetRef?: Ref<Typeahead>;
};

const usePresenter = (props: ChipFieldCombinedProps): ChipFieldPresenterProps => {
  const {
    selectedOptions,
    setSelectedValue,
    setValidChipCount,
    validateChip,
  } = props;

  const [input, setInput] = useState<string>('');
  const [selected, setSelected] = useState<Option[]>(selectedOptions || []);
  const [hasInvalidChip, setHasInvalidChip] = useState<ChipFieldErrorEnum>('None');
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  const targetRef = useRef<Typeahead>(null);

  const updateSelected = () => {
    if (setSelectedValue) {
      setSelectedValue(selected as ChipRecord[]);
    }
  };

  const removeDuplicates = (chips: ChipRecord[]) => {
    // return [...new Map(chips.map(item => [item[key], item])).values()]; //not working due to compiler option --downlevelIteration not being enabled
    const chipToName = chips.map(chip => chip.value);
    return chips.filter((chip, pos) => {
      return chipToName.indexOf(chip.value) === pos;
    });
  };

  const updateValidChipCount = () => {
    const validChips = (selected as ChipRecord[]).filter((chip) => {
      return chip.isValid === true;
    });
    const uniqueValidChips = removeDuplicates(validChips);
    
    if (selected.length - validChips.length > 0) {
      setHasInvalidChip('Error');
    } else {
      setHasInvalidChip('None');
    }
    if (setValidChipCount) {
      setValidChipCount(uniqueValidChips.length);
    }
  };

  useEffect(() => {
    updateSelected();
    updateValidChipCount();
  }, [selected]);


  const handleOnRemoveChip = (index: number) => {
    const newSelected = [...selected];
    newSelected.splice(index, 1);
    setSelected([...newSelected]);
  };

  const handleChangeChipValue = (index: number, option: Option) => {
    const newSelected = [...selected];
    newSelected[index] = option;
    setSelected([...newSelected]);
  };

  const handleRemoveInvalidChips = () => {
    const newSelected = (selected as ChipRecord[]).filter((chip => {
      return chip.isValid === true;
    }));
    setSelected([...newSelected]);
  };

  const handleRenderChip: RenderToken = (option: Option, props: RenderTokenProps, index: number) => {
    let shouldUseOutlineStyle = false;

    if (
      (isInputFocused && hasInvalidChip === 'None') ||
      (
        (option as ChipRecord).isValid &&
          hasInvalidChip === 'Error'
      )
    ) {
      shouldUseOutlineStyle = true;
    }

    const chip : ChipCombinedProps = {
      styleType: shouldUseOutlineStyle ? 'OutlineWhite' : 'White',
      size: 'L',
      type: 'TextIcon',
      state: 'Default',
      error: 'None',
      className: styles.chipContainer,
      mutableChipProps: {
        option: option as ChipRecord,
        index: index,
        tokenProps: props,
        onRemoveChip: (index) => handleOnRemoveChip(index),
        onChangeChipValue: (index, value) => handleChangeChipValue(index, value),
        onValidate: (value) => {
          if (validateChip) {
            return validateChip(value);
          }
          return true; //if no validation function is provided, no error state
        },
      },
      tabIndex: ((index + 1) * 2),
    };

    return <Chip {...chip} />;
  };

  const onInputChanged = (text: string) => {
    setInput(text);
  };

  const saveCurrentInputAsChip = () => {
    if (input && targetRef.current) {
      setSelected(prevState => [...prevState, {
        value: input,
      }]);
      targetRef.current.clear();
      setInput('');
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Tab':
        if (!input.length) {
          break;
        }
        // falls through
      case 'Enter':
      case ',':
      case ';':
        event.preventDefault();
        saveCurrentInputAsChip();
        break;
      case 'Escape':
        if (targetRef.current) {
          targetRef.current.blur();
        }
        break;
      default:
        break;
    }
  };

  const onBlurHandler = () => {
    saveCurrentInputAsChip();
    setIsInputFocused(false);
  };

  const onFocusHandler = () => {
    setIsInputFocused(true);
  };

  return {
    ...props,
    selected: selected,
    error: hasInvalidChip,
    targetRef: targetRef,
    onInputChanged: onInputChanged,
    onKeyDown: onKeyDown,
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    handleRenderChip: handleRenderChip,
    button: {
      ...props.button,
      onClick: handleRemoveInvalidChips,
    },
  };
};

export default usePresenter;
